"use client"

import { useState } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"

interface BiomarkerCardProps {
  name: string
  description?: string | null
  detectionMethod?: "mri" | "blood" | "both" | "ct" | "dexa"
  href: string
  className?: string
}

export function BiomarkerCard({
  name,
  description,
  detectionMethod,
  href,
  className,
}: BiomarkerCardProps) {
  const [expanded, setExpanded] = useState(false)
  const t = useTranslations("ui")

  return (
    <div
      className={cn(
        "rounded-[16px] bg-white p-5 transition-shadow duration-200",
        expanded && "shadow-md",
        className
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="group w-full cursor-pointer text-left"
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-aubergine-900 group-hover:text-aubergine-700 text-sm leading-snug font-medium transition-colors">
            {name}
          </h3>
          <div className="flex flex-shrink-0 items-center gap-1.5">
            {detectionMethod && (
              <span
                className={cn(
                  "type-label-sm inline-block rounded-full px-2 py-0.5 font-semibold tracking-wider uppercase",
                  detectionMethod === "mri" &&
                    "text-deep-blue-950 bg-light-blue-50",
                  detectionMethod === "ct" && "bg-amber-50 text-amber-700",
                  detectionMethod === "dexa" && "bg-stone-100 text-stone-700",
                  detectionMethod === "blood" &&
                    "bg-aubergine-50 text-aubergine-700",
                  detectionMethod === "both" &&
                    "bg-neutral-100 text-neutral-600"
                )}
              >
                {detectionMethod === "both"
                  ? "MRI + Blood"
                  : detectionMethod === "ct"
                    ? "CT"
                    : detectionMethod === "dexa"
                      ? "DEXA"
                      : detectionMethod.toUpperCase()}
              </span>
            )}
            <svg
              className={cn(
                "group-hover:text-aubergine-500 h-4 w-4 flex-shrink-0 text-neutral-300 transition-all duration-200",
                expanded && "text-aubergine-500 rotate-90"
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </button>

      {/* Expanded content — only rendered when clicked */}
      {expanded && (
        <div className="mt-3">
          {description && (
            <p className="text-sm leading-relaxed text-neutral-600">
              {description}
            </p>
          )}
          <a
            href={href}
            className="text-aubergine-600 hover:text-aubergine-800 mt-3 inline-flex items-center gap-1 text-sm font-medium transition-colors"
          >
            {t("readMore")}
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </a>
        </div>
      )}
    </div>
  )
}
